/**
 * What a thing is made of, for the sound hitting it makes (docs/DESIGN_CRASH_SFX.md; owner, Sep 8: "crashing on
 * anything plays the same sound fx").
 *
 * The thing that was hit says so in its own JSON — a `material` tag on the prop, terrain or building. Nothing in the
 * catalogue carries one yet, so a default is read off the tags they already have; tagging the ones the default gets
 * wrong is a JSON edit, never a code change.
 */
import type { Building, Prop, Terrain } from '../content/types';
import type { WorldStream } from '../engine/world-stream';

export const MATERIALS = ['metal', 'glass', 'wood', 'stone', 'plant', 'water', 'body'] as const;
export type Material = (typeof MATERIALS)[number];
/**
 * A stated material is namespaced — `mat_wood`, not `wood`. The plain words are already in use as topics: a pump house
 * is tagged `water` because it is a waterworks, not because a car would splash into it, and half the flower beds are
 * tagged `plant`. The prefix keeps a material a material.
 */
export const MAT_TAG = (m: Material): string => `mat_${m}`;
const KNOWN = new Map<string, Material>(MATERIALS.map((m) => [MAT_TAG(m), m]));

/** the tags a prop already carries → what it is most likely made of (a starting point, not the answer) */
const PROP_DEFAULT: [string, Material][] = [
  ['tree', 'plant'], ['plant', 'plant'],
  ['sign', 'metal'], ['light', 'metal'], ['rail', 'metal'], ['transit', 'metal'], ['mail', 'metal'], ['station', 'metal'],
  ['seat', 'wood'], ['furniture', 'wood'], ['play', 'wood'], ['yard', 'wood'], ['storage', 'wood'],
  ['water', 'water'],
];

/** a tag list's own material, if it names one */
const stated = (tags: string[] | undefined): Material | undefined => { for (const t of tags ?? []) { const m = KNOWN.get(t); if (m) return m; } return undefined; };

export function propMaterial(p: Prop): Material {
  return stated(p.tags) ?? PROP_DEFAULT.find(([t]) => p.tags?.includes(t))?.[1] ?? 'stone';
}
export function buildingMaterial(b: Building): Material {
  return stated(b.tags) ?? (b.tags.includes('style_glass') || b.tags.includes('glass') ? 'glass' : 'stone'); // (a glass tower breaks; brick, adobe and timber-clad all read as masonry from a car)
}
export function terrainMaterial(t: Terrain): Material {
  return stated(t.tags) ?? (t.boatable || t.tags?.includes('water') ? 'water' : 'stone');
}

/**
 * What is on this tile, from the top down: a building's wall wins over the prop standing against it, which wins over
 * the ground. `undefined` means nothing was struck here — open ground.
 */
export function materialAt(w: WorldStream, x: number, y: number): Material | undefined {
  const b = w.blockH(x, y) > 0 ? w.buildingAt(x, y) : undefined;
  if (b) return buildingMaterial(b.def);
  const p = w.propAt(x, y);
  if (p) return propMaterial(p);
  const t = w.terrainAt(x, y);
  if (t && (t.boatable || t.tags?.includes('water'))) return 'water';
  return undefined;
}
